import { useEffect, useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase/client";
import { useI18n } from "../i18n/I18nProvider";
import { TripShareExportModal } from "./TripShareExportModal";
import { getShareWords } from "./shareWords";
import { currentTripLinks, prioritizeShareTrips, type ManagedTripLinks } from "./shareExperience";
import { publicShareUrl } from "./shareLinkUrl";
import { copyShareLink } from "./shareActions";
import { todayInZone } from "./sharedTripTimeline";
import type { TripSummary } from "./tripStore";
import "./TripSharing.css";
import "./ShareExperience.css";
import "./ShareOwnerEditorial.css";
import { sharedPhotoUrl } from "./sharedTripMapModel";
/** The same owner controls are used in both dashboards; stored widget sizes stay untouched. */
export function TripShareWorklet({ uid, trips }: { uid: string; trips: TripSummary[] }) {
  const { language } = useI18n(), words = getShareWords(language);
  const sorted = useMemo(() => prioritizeShareTrips(trips, todayInZone()), [trips]);
  const [selected, setSelected] = useState(""), [open, setOpen] = useState<"create" | "manage" | null>(null);
  const [state, setState] = useState<{ tripId: string; links: ManagedTripLinks["links"] } | null>(null);
  const [unavailable, setUnavailable] = useState(false), [feedback, setFeedback] = useState("");
  const [retry, setRetry] = useState(0);
  const trip = sorted.find(item => item.id === selected) || sorted[0];
  useEffect(() => {
    if (!trip) return;
    let cancelled = false;
    setUnavailable(false); setFeedback("");
    void httpsCallable<{ tripId: string }, ManagedTripLinks>(functions, "listMundelyShares")({ tripId: trip.id }).then(response => {
      if (!cancelled) { if (response.data.schemaVersion !== 2) throw new Error("SHARE_CONTRACT_MISMATCH"); setState({ tripId: trip.id, links: response.data.links }); }
    }).catch(() => { if (!cancelled) setUnavailable(true); });
    return () => { cancelled = true; };
  }, [uid, trip?.id, open, retry]);
  if (!trip) return <p className="share-worklet-empty">{words.noTrips}</p>;
  const known = state?.tripId === trip.id && !unavailable;
  const links = known ? currentTripLinks(state.links) : [];
  const active = links.filter(link => !link.suspended);
  const quickLink = active[0], existing = links.length > 0;
  return <div className="trip-share-worklet share-worklet-v161">
    <div className="share-worklet-photo">
      <img className="share-worklet-cover" src={sharedPhotoUrl(trip.coverPhotoDataUrl) || "/images/travel-window-default.webp"} alt="" onError={event => { if (event.currentTarget.getAttribute("src") !== "/images/travel-window-default.webp") event.currentTarget.src = "/images/travel-window-default.webp"; }}/>
      <label className="share-photo-picker"><span>{words.chooseTrip}</span><select value={trip.id} onChange={event => setSelected(event.target.value)}>{sorted.map(item => <option value={item.id} key={item.id}>{item.name}</option>)}</select></label>
      <div className="share-photo-copy"><span className="share-photo-eyebrow">{words.shareTrip}</span><strong>{trip.name}</strong><span>{trip.destination}</span></div>
    </div>
    <button className="button secondary share-worklet-single-action trip-readonly-allow" type="button" disabled={!unavailable && (!known || (existing && !quickLink) || (!!trip.archivedAt && !quickLink))} onClick={() => {
      if (unavailable) { setRetry(value=>value+1); return; }
      if (quickLink) { void copyShareLink(publicShareUrl(quickLink.token)).then(copied=>setFeedback(copied ? words.copyDone : words.copyFailed)); }
      else if (known && !existing && !trip.archivedAt) setOpen("create");
    }}>{unavailable ? (language==="it" ? "Riprova" : language==="es" ? "Reintentar" : "Retry") : !known ? words.loadingLinks : existing ? words.copyAction : language==="it" ? "Crea link" : language==="es" ? "Crear enlace" : "Create link"}</button>
    {unavailable && <p role="status">{words.backendUnavailable}</p>}
    {known && existing && !quickLink && <p role="status">{words.suspended}</p>}
    {trip.archivedAt && <p>{words.archive}</p>}{feedback && <p role="status">{feedback}</p>}
    {open && <TripShareExportModal uid={uid} trip={trip} mode="share" initialMode={open} onClose={() => setOpen(null)} />}
  </div>;
}
